import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { AxiosResponse } from 'axios';

import { downloadCsv } from 'Components/DownloadCSV/useDownloadCsvPolling/useDownloadCsvPolling.helpers';
import { logger } from 'Services/logger/logger';
import useFeatureToggle, { FEATURES } from 'Utilities/hooks/useFeatureToggle';

import { formatDownloadCsvFilename } from '../downloadCSV.helpers';
import { PollingState } from '../DownloadCSV.types';
import type { AbortedPollingState } from '../useDownloadCsvAbortPolling/useDownloadCsvAbortPolling';
import { useDownloadCsvAbortPolling } from '../useDownloadCsvAbortPolling/useDownloadCsvAbortPolling';

const DEFAULT_POLLING_RETRIES_LIMIT = 49;
const INTERVAL_IN_SECS = 6000; // 6 seconds

export type UseDownloadCsvPollingProps = {
	fetchCsv: () => Promise<AxiosResponse>;
	filenameTitle: string;
	generateCsv: () => Promise<void>;
	queryKey: string[];
	retriesLimit?: number;
};

type UseDownloadCsvPollingApi = {
	startPolling: () => Promise<void>;
	state: PollingState;
};

const logScope = 'useDownloadCsvPolling';

const useDownloadCsvPolling = ({
	fetchCsv,
	filenameTitle,
	generateCsv,
	queryKey,
	retriesLimit = DEFAULT_POLLING_RETRIES_LIMIT,
}: UseDownloadCsvPollingProps): UseDownloadCsvPollingApi => {
	const [retryCount, setRetryCount] = useState<number>(0);
	const [state, setState] = useState<PollingState>(PollingState.Inactive);
	const isBulkBiddingCsvEnabled = useFeatureToggle(FEATURES.bulkBiddingCsv);

	const onAbortPolling = (abortedPollingState: AbortedPollingState) => setState(abortedPollingState);

	useDownloadCsvAbortPolling({ onAbortPolling, state });

	const { refetch } = useQuery({
		enabled: state === PollingState.Polling,
		onError: async () => {
			await setState(PollingState.Error);
		},
		queryFn: async () => {
			const response = await fetchCsv();

			if (response.status === 200) {
				await setState(PollingState.Success);

				await downloadCsv({
					content: response.data,
					filename: formatDownloadCsvFilename(filenameTitle),
					shouldAppendBulkBiddingColumn: isBulkBiddingCsvEnabled,
				});
			}

			if (response.status === 202) {
				await setRetryCount((prevRetryCount) => prevRetryCount + 1);
			}

			if (![202, 200].includes(response.status)) {
				const message = `CSV file generation failed. Returned ${response.status} response status, which is not supported.`;
				const error = new Error(message);
				logger.error({ error, message, scope: logScope });

				await setState(PollingState.Error);
			}

			if (retryCount >= retriesLimit) {
				const message = 'CSV file generation failed. Limit of download retries has been exceeded.';
				const error = new Error(message);
				logger.error({ error, message, scope: logScope });

				await setState(PollingState.RetriesLimitExceeded);
			}

			return response;
		},
		queryKey,
		refetchInterval: INTERVAL_IN_SECS,
		refetchOnWindowFocus: false,
		retry: false,
	});

	const resetCountsAndStartPolling = async () => {
		setRetryCount(0);
		await setState(PollingState.Polling);
		await refetch();
	};

	const startPolling = async () => {
		await setState(PollingState.Inactive);
		await generateCsv();
		await resetCountsAndStartPolling();
	};

	useEffect(() => {
		let timer: NodeJS.Timeout;

		if (state === PollingState.AbortedFilter || state === PollingState.AbortedPage) {
			timer = setTimeout(() => {
				setState(PollingState.Inactive);
			}, 5000);
		}

		return () => {
			clearTimeout(timer);
		};
	}, [state]);

	return { startPolling, state };
};

export default useDownloadCsvPolling;
